import {
  useEffect,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { ChallengeDetail } from "../../challenges/types/challengeAdmin";
import type {
  DraftPair,
  DraftPlayer,
  PairMode,
} from "../../challenges/types/challengeDraft";
import {
  createPairId,
  normalizeCpf,
  sideChoiceToDraftSide,
} from "../../challenges/utils/challengePairs";
import type { AdminEventRegistration } from "../services/eventRegistrationAdminService";
import {
  limitPairsForChallengeType,
  normalizeParticipantCpfs,
} from "../utils/adminChallengeEditor";

type UseAdminChallengeRegistrationSelectionSyncInput = {
  activeRegistrationCategoryKeyForModal: string;
  challengeDetail: ChallengeDetail | null;
  challengeFixedPairRegistrations: AdminEventRegistration[];
  challengeIndividualRegistrations: AdminEventRegistration[];
  currentPairMode: PairMode;
  draftPairs: DraftPair[];
  isSuper8: boolean;
  isTournamentChallenge: boolean;
  nameByCpf: Record<string, string>;
  removedTournamentPairKeysRef: MutableRefObject<Set<string>>;
  setDraftPairs: Dispatch<SetStateAction<DraftPair[]>>;
  setDraftPlayers: Dispatch<SetStateAction<DraftPlayer[]>>;
  setParticipantsFromPairs: (nextPairs: DraftPair[]) => void;
  setParticipantsFromPlayers: (nextPlayers: DraftPlayer[]) => void;
  showChallengeModal: boolean;
  showErrorToast: (message: string) => void;
};

export function useAdminChallengeRegistrationSelectionSync({
  activeRegistrationCategoryKeyForModal,
  challengeDetail,
  challengeFixedPairRegistrations,
  challengeIndividualRegistrations,
  currentPairMode,
  draftPairs,
  isSuper8,
  isTournamentChallenge,
  nameByCpf,
  removedTournamentPairKeysRef,
  setDraftPairs,
  setDraftPlayers,
  setParticipantsFromPairs,
  setParticipantsFromPlayers,
  showChallengeModal,
  showErrorToast,
}: UseAdminChallengeRegistrationSelectionSyncInput) {
  const sideFromRegistration = (row: AdminEventRegistration) =>
    sideChoiceToDraftSide(row.preferredSide, {
      preferenceMissing: !row.preferredSide,
    });

  useEffect(() => {
    if (!showChallengeModal || isSuper8 || currentPairMode !== "fixed") return;
    if (isTournamentChallenge) {
      const removedPairKeys = removedTournamentPairKeysRef.current;
      const mappedPairs = challengeFixedPairRegistrations.reduce<DraftPair[]>(
        (acc, row) => {
          const cpf1 = normalizeCpf(row.userCpf);
          const cpf2 = normalizeCpf(row.partnerCpf);
          if (cpf1.length < 8 || cpf2.length < 8 || cpf1 === cpf2) return acc;
          const pairId = createPairId(cpf1, cpf2);
          const rowTournamentId = Number(row.tournamentId || challengeDetail?.id || 0);
          const rowCategoryKey = String(
            row.registrationCategoryKey ||
              activeRegistrationCategoryKeyForModal ||
              "",
          ).trim();
          if (
            removedPairKeys.has(
              `${rowTournamentId}::${rowCategoryKey}::${pairId}`,
            )
          ) {
            return acc;
          }
          const player1 =
            String(row.userName || "").trim() ||
            nameByCpf[cpf1] ||
            siteTexts.challengeEngine.labels.player1;
          const player2 =
            String(row.partnerName || "").trim() ||
            nameByCpf[cpf2] ||
            siteTexts.challengeEngine.labels.player2;
          acc.push({
            id: pairId,
            cpf1,
            cpf2,
            label: `${player1} / ${player2}`,
          });
          return acc;
        },
        [],
      );
      const uniquePairs = Array.from(
        new Map(mappedPairs.map((pair) => [pair.id, pair])).values(),
      );
      const nextPairsToApply = uniquePairs;
      setDraftPlayers([]);
      setDraftPairs((prev) => {
        const unchanged =
          nextPairsToApply.length === prev.length &&
          nextPairsToApply.every((pair, index) => {
            const current = prev[index];
            if (!current) return false;
            return (
              current.id === pair.id &&
              current.cpf1 === pair.cpf1 &&
              current.cpf2 === pair.cpf2 &&
              current.label === pair.label
            );
          });
        return unchanged ? prev : nextPairsToApply;
      });
      const nextCpfs = normalizeParticipantCpfs(
        nextPairsToApply.flatMap((pair) => [pair.cpf1, pair.cpf2]),
      );
      const currentCpfs = normalizeParticipantCpfs(
        challengeDetail?.participantCpfs,
      );
      const shouldSyncParticipants =
        nextCpfs.length !== currentCpfs.length ||
        nextCpfs.some((cpf, index) => cpf !== currentCpfs[index]);
      if (shouldSyncParticipants) {
        setParticipantsFromPairs(nextPairsToApply);
      }
      return;
    }
    if (challengeFixedPairRegistrations.length === 0) return;
    let nextPairsToApply: DraftPair[] | null = null;
    let shouldSyncParticipants = false;
    let didTrimToLimit = false;
    const customPocoPairLimit =
      String(challengeDetail?.challengeType || "").trim().toLowerCase() ===
        "poco"
        ? Math.floor(
            Number(
              challengeDetail?.playersCount ||
                (Number(challengeDetail?.courtsCount) === 4 ? 16 : 12),
            ) / 2,
          )
        : null;
    setDraftPairs((prev) => {
      const byId = new Map(prev.map((pair) => [pair.id, pair]));
      challengeFixedPairRegistrations.forEach((row) => {
        const cpf1 = normalizeCpf(row.userCpf);
        const cpf2 = normalizeCpf(row.partnerCpf);
        if (cpf1.length < 8 || cpf2.length < 8 || cpf1 === cpf2) return;
        const pairId = createPairId(cpf1, cpf2);
        if (byId.has(pairId)) return;
        const player1 =
          String(row.userName || "").trim() ||
          nameByCpf[cpf1] ||
          siteTexts.challengeEngine.labels.player1;
        const player2 =
          String(row.partnerName || "").trim() ||
          nameByCpf[cpf2] ||
          siteTexts.challengeEngine.labels.player2;
        byId.set(pairId, {
          id: pairId,
          cpf1,
          cpf2,
          label: `${player1} / ${player2}`,
        });
      });
      const mergedPairsRaw = Array.from(byId.values());
      const mergedPairs =
        customPocoPairLimit !== null
          ? mergedPairsRaw.slice(0, customPocoPairLimit)
          : limitPairsForChallengeType(
              mergedPairsRaw,
              challengeDetail?.challengeType,
            );
      didTrimToLimit = mergedPairs.length < mergedPairsRaw.length;
      const mergedCpfs = Array.from(
        new Set(
          mergedPairs
            .flatMap((pair) => [
              normalizeCpf(pair.cpf1),
              normalizeCpf(pair.cpf2),
            ])
            .filter((cpf) => cpf.length >= 8),
        ),
      );
      const currentCpfs = normalizeParticipantCpfs(
        challengeDetail?.participantCpfs,
      );
      shouldSyncParticipants =
        mergedCpfs.length !== currentCpfs.length ||
        mergedCpfs.some((cpf, index) => cpf !== currentCpfs[index]);
      nextPairsToApply = mergedPairs;
      const unchanged =
        mergedPairs.length === prev.length &&
        mergedPairs.every((pair, index) => {
          const current = prev[index];
          if (!current) return false;
          return (
            current.id === pair.id &&
            current.cpf1 === pair.cpf1 &&
            current.cpf2 === pair.cpf2 &&
            current.label === pair.label
          );
        });
      if (unchanged) return prev;
      return mergedPairs;
    });
    if (nextPairsToApply && shouldSyncParticipants) {
      setParticipantsFromPairs(nextPairsToApply);
    }
    if (didTrimToLimit) {
      const pairLimit = customPocoPairLimit ?? 6;
      showErrorToast(
        `Este desafio permite no máximo ${pairLimit} duplas (${pairLimit * 2} participantes).`,
      );
    }
  }, [
    activeRegistrationCategoryKeyForModal,
    challengeDetail?.id,
    challengeDetail?.challengeType,
    challengeDetail?.challengeModel,
    challengeDetail?.participantCpfs,
    challengeDetail?.playersCount,
    challengeDetail?.courtsCount,
    challengeFixedPairRegistrations,
    currentPairMode,
    draftPairs,
    isSuper8,
    isTournamentChallenge,
    nameByCpf,
    removedTournamentPairKeysRef,
    setDraftPairs,
    setDraftPlayers,
    setParticipantsFromPairs,
    showChallengeModal,
    showErrorToast,
  ]);

  useEffect(() => {
    if (!showChallengeModal) return;
    if (!isSuper8 && currentPairMode === "fixed") return;
    if (isTournamentChallenge) {
      const nextPlayers = challengeIndividualRegistrations.map((row) => {
        const cpf = normalizeCpf(row.userCpf);
        return {
          cpf,
          name:
            String(row.userName || "").trim() ||
            nameByCpf[cpf] ||
            siteTexts.challengeEngine.labels.player,
          side: sideFromRegistration(row),
        } satisfies DraftPlayer;
      });
      const uniquePlayers = Array.from(
        new Map(
          nextPlayers.map((player) => [normalizeCpf(player.cpf), player]),
        ).values(),
      );
      setDraftPairs([]);
      setDraftPlayers(uniquePlayers);
      setParticipantsFromPlayers(uniquePlayers);
      return;
    }
    if (challengeIndividualRegistrations.length === 0) return;
    let nextPlayersToApply: DraftPlayer[] | null = null;
    setDraftPlayers((prev) => {
      const byCpf = new Map(
        prev.map((player) => [normalizeCpf(player.cpf), player]),
      );
      let changed = false;
      challengeIndividualRegistrations.forEach((row) => {
        const cpf = normalizeCpf(row.userCpf);
        if (cpf.length < 8 || byCpf.has(cpf)) return;
        const name =
          String(row.userName || "").trim() ||
          nameByCpf[cpf] ||
          siteTexts.challengeEngine.labels.player;
        byCpf.set(cpf, {
          cpf,
          name,
          side: sideFromRegistration(row),
        });
        changed = true;
      });
      if (!changed) return prev;
      nextPlayersToApply = Array.from(byCpf.values());
      return nextPlayersToApply;
    });
    if (nextPlayersToApply) {
      setParticipantsFromPlayers(nextPlayersToApply);
    }
  }, [
    challengeIndividualRegistrations,
    currentPairMode,
    isSuper8,
    isTournamentChallenge,
    nameByCpf,
    setDraftPairs,
    setDraftPlayers,
    setParticipantsFromPlayers,
    showChallengeModal,
  ]);
}
